import * as React from "react";
import { Box, Absolute } from "../box";
import { ZStack } from "../stack";
import { Text } from "../text";
import { Stack } from "@chakra-ui/core";
import { MotionBox } from "../motion";

type Props = {
	orientation?: "vertical" | "horizontal";
	barWidth?: number;
	barHeight?: number;
	label?: string;
	accessoryItems?: {
		content: any;
		value: number;
	}[];
	value?: number | { min: number; max: number };
	range: { min: number; max: number };
	units?: string;
	formatter?: (value: number) => string;
	color?: string;
	bgColor?: string;
	gradient?: string[];
	animation?: any;
	disabled?: boolean;
};

// const RefBox = React.forwardRef((props, ref) => (
// 	<Box innerRef={ref} {...props} />
// ))
// const MotionBox: React.FC<any> = motion.custom(RefBox)

const Tick: React.FC<any> = (props: any): React.ReactElement => (
	<Box position="absolute" bg="brand.gray.700" width={"2px"} height={"2px"} {...props} />
);

const AccessoryItem: React.FC<any> = (props: any): React.ReactElement => {
	const {
		key,
		orientation,
		side,
		content,
		tickSize,
		offset,
		valueOffset,
		labelTransform,
		...rest
	} = props;

	let tickContent = content;
	let tickProps = {};
	let contentProps = {};

	if (orientation === "horizontal") {
		tickProps = {
			height: `${tickSize}px`,
			bottom: side === "top" ? 0 : null,
			top: side === "bottom" ? 0 : null
		};
		contentProps = {
			bottom: side === "top" ? `${tickSize + 2}px` : null,
			top: side === "bottom" ? `${tickSize + 6}px` : null,
			transform: labelTransform || "translateX(-50%)"
		};
	} else {
		tickProps = {
			width: `${tickSize}px`,
			right: side === "left" ? 0 : null,
			left: side === "right" ? 0 : null
		};
		contentProps = {
			right: side === "left" ? `${tickSize + 6}px` : null,
			left: side === "right" ? `${tickSize + 6}px` : null,
			transform: labelTransform || "translateY(-50%)"
		};
	}

	if (typeof tickContent === "string") {
		tickContent = (
			<Text mb="-5px" variant="value" fontSize="xl" color="text.base.tertiary">
				{content}
			</Text>
		);
	}

	return (
		<MotionBox position="absolute" key={key} overflow="visible" {...rest}>
			<Tick {...tickProps} />
			<Absolute {...contentProps}>{tickContent}</Absolute>
		</MotionBox>
	);
};

const ValueBar: React.FC<any> = (props: any): React.ReactElement => {
	const {
		orientation,
		inset,
		valueOffset,
		valueHeight,
		valueProps,
		valueAnimation,
		disabled,
		bgColor = "bg.levels.2",
		children,
		...rest
	} = props;

	const size = parseInt(
		(orientation === "horizontal" ? props.height : props.width).replace(/[^0-9]/, ""),
		10
	);
	let containerProps = {};
	let barProps = {};

	if (orientation === "horizontal") {
		containerProps = {
			left: 0,
			right: 0
		};
		barProps = {
			height: "100%",
			top: `${inset}px`,
			bottom: `${inset}px`,
			left: `${valueOffset}px`,
			initial: {
				width: 0
			},
			animate: {
				width: valueHeight
			}
		};
	} else {
		containerProps = {
			top: 0,
			bottom: 0
		};
		barProps = {
			width: "100%",
			left: `${inset}px`,
			right: `${inset}px`,
			bottom: `${valueOffset}px`,
			initial: {
				height: 0
			},
			animate: {
				height: valueHeight
			}
		};
	}

	barProps = { ...valueProps, ...barProps };

	const accessoryElements = React.Children.map(children, (child: any) => {
		if (child) {
			const childProps = { ...child.props } || {};

			if (orientation === "horizontal") {
				// childProps.transform = 'translateX(-50%)'
				if (childProps.side === "top") {
					childProps.bottom = `${size + 4}px`;
				} else {
					childProps.top = `${size + 4}px`;
				}
			} else {
				childProps.transform = "translateY(50%)";
				if (childProps.side === "left") {
					childProps.right = `${size + 4}px`;
				} else {
					childProps.left = `${size + 4}px`;
				}
			}

			return React.cloneElement(child, childProps);
		}
		return child;
	});

	return (
		<ZStack position="absolute" width={props.width} height={props.height} {...containerProps}>
			<Box anchor="fill" width="100%" height="100%" rounded="full" bg={bgColor} />
			{disabled !== true && (
				<MotionBox
					rounded="full"
					{...barProps}
					transition={{
						type: "spring",
						damping: 40,
						stiffness: 200,
						...valueAnimation
					}}
				/>
			)}
			{disabled !== true && accessoryElements}
		</ZStack>
	);
};

const getLabel = (
	label: string,
	units: string,
	disabled: boolean,
	inline: boolean = false
): any => {
	return (
		<Stack isInline={inline} spacing={inline ? 2 : 1} align="baseline">
			<Text
				mt={inline ? 0 : 3}
				mb={0}
				width={inline ? "auto" : "100%"}
				variant="label"
				fontSize="sm"
				color={disabled ? "text.base.tertiary" : "text.base.primary"}
				textAlign="center"
			>
				{label}
			</Text>
			{units && (
				<Text
					m={0}
					width={inline ? "auto" : "100%"}
					variant="label"
					fontSize="xs"
					fontWeight="semibold"
					textAlign="center"
					color={disabled ? "text.base.tertiary" : "text.base.econdary"}
				>
					{units}
				</Text>
			)}
		</Stack>
	);
};

const LinearMeter: React.FC<Props> = (props: Props): React.ReactElement => {
	const {
		orientation,
		barWidth,
		barHeight,
		label,
		value,
		range,
		units,
		formatter,
		color,
		bgColor = "bg.100",
		gradient,
		animation,
		accessoryItems,
		disabled,
		...rest
	} = props;

	let isRange = false;
	let valueRange = range;
	if (typeof value !== "object") {
		valueRange = { min: range.min, max: value };
	} else {
		valueRange = value;
		isRange = true;
	}

	let valueFormatter =
		typeof formatter === "function"
			? formatter
			: (value: number): string => {
					return `${value}`;
			  };

	const isHoriz = orientation === "horizontal";
	let width = isHoriz ? 250 : 20;
	let height = isHoriz ? 20 : 280;
	if (barWidth && barWidth > 0) {
		width = barWidth;
	}
	if (barHeight && barHeight > 0) {
		height = barHeight;
	}

	const inset = 2;
	const outerOffset = Math.round(width / 2) + inset * 2;
	const tickWidth = 10;
	const size = isHoriz ? width : height;
	const barPos = {
		min: {
			progress: 0,
			top: 0,
			bottom: 0,
			x: 0,
			y: 0,
			label: {
				transform: undefined
			}
		},
		max: {
			progress: 0,
			top: 0,
			bottom: 0,
			x: 0,
			y: 0,
			label: {
				transform: undefined
			}
		}
	};

	const accessoryHeight = accessoryItems && accessoryItems.length > 0 ? 50 : 0;
	const container = {
		width: isHoriz ? width + 80 : 150,
		height: isHoriz ? 70 + accessoryHeight : height,
		anchor: isHoriz && accessoryHeight === 0 ? "center-x" : "center",
		bottom: isHoriz && accessoryHeight === 0 ? 0 : "auto"
	};

	const lw = isHoriz ? 80 : 40;
	["min", "max"].forEach(key => {
		const val = valueRange ? valueRange[key] : 0;
		const clampedValue = Math.min(range.max, Math.max(range.min, val));
		const progress = (clampedValue - range.min) / (range.max - range.min);
		const offset = (size - inset * 2) * progress;
		barPos[key].progress = progress;
		barPos[key].top = size - offset - inset;
		barPos[key].bottom = size - barPos[key].top;
		barPos[key].x = isHoriz ? barPos[key].bottom - lw / 2 : 0;
		barPos[key].y = isHoriz ? 0 : barPos[key].bottom - lw / 2;
	});

	const valueLabels = isRange ? ["min", "max"] : ["max"];
	const valueHeight = barPos.min.top - barPos.max.top;
	const labelColor = disabled ? "text.base.tertiary" : "text.base.primary";
	const animationDelay = animation ? animation.delay : 0;

	if (valueLabels.length === 2) {
		const min = barPos.min;
		const max = barPos.max;

		// determine if the min/max value content will overlay based on assumed width and their
		// positions; if so, we need to shift them accordingly
		if (isHoriz) {
			const minRange = { l: min.x, r: min.x + lw };
			const maxRange = { l: max.x, r: max.x + lw };

			if (minRange.r >= maxRange.l) {
				barPos.min.label.transform = "translateX(-100%)";
				barPos.max.label.transform = "translateX(3px)";
			}
		} else {
			const minRange = { t: min.y, b: min.y + lw };
			const maxRange = { t: max.y, b: max.y + lw };

			if (minRange.b >= maxRange.t) {
				barPos.min.label.transform = "translateY(-20%)";
				barPos.max.label.transform = "translateY(-70%)";
			}
		}
	}

	const valueProps: any = {};
	if (gradient && gradient instanceof Array) {
		valueProps.backgroundImage = `linear-gradient(to ${isHoriz ? "right" : "top"}, ${gradient.join(
			","
		)})`;
		valueProps.backgroundPosition = isHoriz ? "left center" : "center bottom";
		valueProps.backgroundSize = isHoriz
			? `${Math.ceil(valueHeight)}px ${height}px`
			: `${width}px ${Math.ceil(valueHeight)}px`;
		valueProps.backgroundRepeat = "no-repeat";
	} else {
		valueProps.bg = color;
	}

	return (
		<Box {...rest}>
			{isHoriz === true && getLabel(label, units, disabled, true)}
			<ZStack width={`${container.width}px`} height={`${container.height}px`} mx="auto">
				<ValueBar
					orientation={orientation}
					width={`${width}px`}
					height={`${height}px`}
					anchor={container.anchor}
					bottom={container.bottom}
					inset={inset}
					valueOffset={barPos.min.bottom}
					valueHeight={valueHeight}
					valueProps={valueProps}
					valueAnimation={animation}
					bgColor={bgColor}
					disabled={disabled}
				>
					{valueLabels.map((key, index) => (
						<AccessoryItem
							key={`value-${key}`}
							left={isHoriz ? `${barPos[key].bottom}px` : null}
							bottom={isHoriz ? null : `${barPos[key].bottom}px`}
							orientation={orientation}
							side={isHoriz ? "top" : "right"}
							tickSize={tickWidth}
							labelTransform={barPos[key].label.transform}
							content={
								<Stack alignItems="baseline" spacing="2px" isInline>
									<Text mt={0} mb="-5px" variant="value" fontSize="2xl">
										{valueFormatter(valueRange[key])}
									</Text>
									{units && (
										<Text
											m={0}
											fontSize="sm"
											color={disabled ? "text.base.tertiary" : "text.base.secondary"}
										>
											{units}
										</Text>
									)}
								</Stack>
							}
							animation={{
								initial: {
									opacity: 0,
									y: 10
								},
								animate: {
									opacity: 1,
									y: 0
								},
								transition: {
									type: "spring",
									damping: 15,
									stiffness: 200,
									delay: 0.5 + index * 0.1 + animationDelay
								}
							}}
						/>
					))}
					{accessoryItems &&
						accessoryItems.map((item, index) => {
							const pos = (item.value - range.min) / (range.max - range.min);
							const offset = (size - inset * 2) * pos;
							let content = item.content;

							if (typeof content === "string") {
								content = (
									<Text variant="value" fontSize="xl" color="text.base.tertiary">
										{content}
									</Text>
								);
							}

							return (
								<AccessoryItem
									key={`accessory-${index}`}
									left={isHoriz ? `${offset}px` : null}
									bottom={isHoriz ? null : `${offset}px`}
									orientation={orientation}
									side={isHoriz ? "bottom" : "left"}
									tickSize={tickWidth}
									content={content}
									animation={{
										initial: {
											opacity: 0,
											y: isHoriz ? -10 : 10
										},
										animate: {
											opacity: 1,
											y: 0
										},
										transition: {
											type: "spring",
											damping: 50,
											stiffness: 50,
											delay: 0.7 + index * 0.1 + animationDelay
										}
									}}
								/>
							);
						})}
				</ValueBar>
			</ZStack>
			{isHoriz === false && getLabel(label, units, disabled, false)}
		</Box>
	);
};

LinearMeter.defaultProps = {
	orientation: "vertical",
	value: 0,
	range: {
		min: 0,
		max: 1
	},
	color: "#333",
	disabled: false
};

export default LinearMeter;
